import * as THREE from 'three';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { isDecapitation } from '../combat/blood-model.js';

/** Shake strength 0 to 1 per kind of blow; `trauma` decays on real time and the offset follows its square. */
export const SHAKE = { crit: .38, kill: .55, decapitation: 1, rage: .22, overload: .26 } as const;
export type ShakeKind = keyof typeof SHAKE;

/** How a blow shakes the screen: beheadings most, then any other killing blow, then a Rage going off, then a critical; plain hits and misses not at all. */
export function shakeFor(event: CombatVisualEvent): ShakeKind | undefined {
  // A Rage going off (God's Strength) thumps the ground like a heavy blow.
  if (event.kind === 'ability' && event.abilityId === 'rage') return 'rage';
  // So does an Overload slammed into the ground.
  if (event.kind === 'ability' && event.abilityId === 'overload') return 'overload';
  if (event.kind !== 'impact' || event.damage <= 0) return undefined;
  if (isDecapitation(event)) return 'decapitation';
  if (event.lethal && event.critical) return 'kill';
  return event.critical ? 'crit' : undefined;
}

const DECAY = 1.6;      // trauma lost per second
const MAX_OFFSET = .55;  // world units at full trauma
const MAX_ROLL = .012;   // radians at full trauma
const FREQUENCY = 26;    // noise cycles per second

const hash = (n: number) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
/** Smooth 1D value noise in [-1, 1]. */
export const noise = (t: number, seed: number) => {
  const i = Math.floor(t), f = t - i, u = f * f * (3 - 2 * f);
  return (hash(i + seed * 17.3) * (1 - u) + hash(i + 1 + seed * 17.3) * u) * 2 - 1;
};

/**
 * Screen shake for heavy blows. Presentation only: it keeps a single trauma value that blows add to and time drains, and
 * `apply`/`restore` bracket the render so the camera the controls move is never left displaced.
 */
export class ScreenShake {
  private trauma = 0;
  private time = 0;
  private offset = new THREE.Vector3();
  private applied = false;
  private rollApplied = 0;
  enabled = true;

  get level() { return this.trauma; }
  /** Adds a blow; trauma saturates at 1, so a pile of hits never shakes harder than a beheading. */
  add(kind: ShakeKind | number) { this.trauma = Math.min(1, this.trauma + (typeof kind === 'number' ? kind : SHAKE[kind])); }
  clear() { this.trauma = 0; }
  /** The offset (camera-right, camera-up, roll) at the current trauma and time. */
  sample(): { x: number; y: number; roll: number } {
    const power = this.trauma * this.trauma, t = this.time * FREQUENCY;
    return { x: noise(t, 1) * power * MAX_OFFSET, y: noise(t, 2) * power * MAX_OFFSET, roll: noise(t, 3) * power * MAX_ROLL };
  }
  /** Advances by `dt` real seconds. */
  update(dt: number) { dt = Math.min(Math.max(dt, 0), .1); this.time += dt; this.trauma = Math.max(0, this.trauma - DECAY * dt); }
  /** Displaces the camera along its own axes for the coming render. */
  apply(camera: THREE.Camera) {
    if (!this.enabled || this.trauma <= 0 || this.applied) return;
    const { x, y, roll } = this.sample();
    camera.updateMatrixWorld();
    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0), up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
    this.offset.copy(right).multiplyScalar(x).addScaledVector(up, y);
    camera.position.add(this.offset); camera.rotateZ(roll); this.rollApplied = roll; this.applied = true;
    camera.updateMatrixWorld(true);
  }
  /** Puts the camera back exactly where the controls left it. */
  restore(camera: THREE.Camera) {
    if (!this.applied) return;
    camera.rotateZ(-this.rollApplied); camera.position.sub(this.offset); this.applied = false;
    camera.updateMatrixWorld(true);
  }
}
